import { useMemo, useState } from "react"
import { Box, Grid, HStack, Stack, Table, Input } from "@chakra-ui/react"
import { Text } from "@/components"
import { CapTableFunding, CapTableNote, CapTableRoundComparison, CapTableMitigation } from "@/types"
import { upsertCapTableMitigation, deleteCapTableMitigation } from "@/api/companies"
import { useQueryClient } from "@tanstack/react-query"

interface Props {
  companyId: number
  capTableNote: CapTableNote | null
  capTableFunding: CapTableFunding[]
  capTableComparisons?: CapTableRoundComparison[]
  capTableMitigations?: CapTableMitigation[]
}

export const CapTableView = ({ companyId, capTableNote, capTableFunding, capTableComparisons, capTableMitigations }: Props) => {
  const queryClient = useQueryClient()
  const [editingMitigationId, setEditingMitigationId] = useState<number | null>(null)
  const [mitigationMemo, setMitigationMemo] = useState("")
  const [savingMitigation, setSavingMitigation] = useState(false)

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "N/A"
    const num = typeof value === "string" ? parseFloat(value) : value
    if (isNaN(num)) return String(value)
    return num.toLocaleString()
  }

  const formatCurrency = (value: string | null | undefined) => {
    if (!value) return "N/A"
    const num = parseFloat(value)
    if (isNaN(num)) return value
    return `$${num.toLocaleString()}`
  }

  const sortedFunding = useMemo(() => {
    return [...capTableFunding].sort(
      (a, b) => new Date(b.issueDate || 0).getTime() - new Date(a.issueDate || 0).getTime()
    )
  }, [capTableFunding])

  const comparisonMap = useMemo(() => {
    if (!capTableComparisons) return new Map<number, CapTableRoundComparison>()
    return new Map(capTableComparisons.map((c) => [c.fundingId, c]))
  }, [capTableComparisons])

  const mitigationMap = useMemo(() => {
    if (!capTableMitigations) return new Map<number, CapTableMitigation>()
    return new Map(capTableMitigations.map((m) => [m.fundingId, m]))
  }, [capTableMitigations])

  const dirColor = (d: "Up" | "Down" | "Flat" | null) =>
    d === "Up" ? "green.600" : d === "Down" ? "red.600" : "gray.500"

  const handleSaveMitigation = async (fundingId: number) => {
    if (!mitigationMemo.trim()) return
    setSavingMitigation(true)
    try {
      await upsertCapTableMitigation(companyId, fundingId, mitigationMemo.trim())
      queryClient.invalidateQueries({ queryKey: ["companyInfo", companyId] })
      setEditingMitigationId(null)
      setMitigationMemo("")
    } catch (err) {
      console.error("Failed to save mitigation:", err)
    } finally {
      setSavingMitigation(false)
    }
  }

  const handleRemoveMitigation = async (fundingId: number) => {
    setSavingMitigation(true)
    try {
      await deleteCapTableMitigation(companyId, fundingId)
      queryClient.invalidateQueries({ queryKey: ["companyInfo", companyId] })
    } catch (err) {
      console.error("Failed to remove mitigation:", err)
    } finally {
      setSavingMitigation(false)
    }
  }

  const NoteItem = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
    <Box>
      <Text fontSize="xs" color="text.muted" fontWeight="medium">
        {label}
      </Text>
      <Text fontSize="sm">{value || "N/A"}</Text>
    </Box>
  )

  return (
    <Stack gap={6}>
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        {capTableNote ? (
          <>
            <Box mb={5}>
              <Text fontSize="xs" color="text.muted" fontWeight="medium">
                Fully Diluted Valuation
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="blue.600">
                {formatCurrency(String(Number(capTableNote.totalAuthShares) * Math.max(Number(capTableNote.fmv), Number(capTableNote.esoLrp))))}
              </Text>
            </Box>

            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              <NoteItem label="Founding Date" value={formatDate(capTableNote.foundingDate)} />
              <NoteItem label="Total Auth Shares" value={formatNumber(capTableNote.totalAuthShares)} />
              <NoteItem label="Employee Count" value={formatNumber(capTableNote.employeeCount)} />
              <NoteItem label="Revenue" value={formatCurrency(capTableNote.revenue?.toString())} />
              <NoteItem label="Geography" value={capTableNote.geography} />
              <NoteItem label="Sector" value={capTableNote.sector} />
              <NoteItem label="FMV Source" value={capTableNote.fmvSource} />
              <NoteItem label="FMV" value={formatCurrency(capTableNote.fmv)} />
              <NoteItem label="Div Date" value={formatDate(capTableNote.divDate)} />
              <NoteItem label="All VC" value={capTableNote.allVc} />
              <NoteItem label="Last VC" value={capTableNote.lastVc} />
              <NoteItem label="Is Profitable" value={capTableNote.isProfitable === 1 ? "Yes" : "No"} />
              <NoteItem label="LTM Profit/Loss" value={formatCurrency(capTableNote.ltmProfitLoss?.toString())} />
              <NoteItem label="# Top VCs (LR)" value={formatNumber(capTableNote.numTopVcsLr)} />
              <NoteItem label="# Special VCs (LR)" value={formatNumber(capTableNote.numSpecialVcsLr)} />
              <NoteItem label="LRP" value={formatCurrency(capTableNote.lrp)} />
              <NoteItem label="ESO LRP" value={formatCurrency(capTableNote.esoLrp)} />
              <NoteItem label="Last Round Date" value={formatDate(capTableNote.lastRoundDate)} />
              <NoteItem label="Last Round Row Num" value={formatNumber(capTableNote.lastRoundRowNum)} />
              <NoteItem label="Edited By" value={capTableNote.editedBy} />
              <NoteItem label="Modified Date" value={formatDate(capTableNote.updatedAt)} />
            </Grid>

            {capTableNote.notes && (
              <Box mt={4}>
                <Text fontSize="xs" color="text.muted" fontWeight="medium">
                  Notes
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {capTableNote.notes}
                </Text>
              </Box>
            )}
          </>
        ) : (
          <Text color="text.muted">No cap table notes found.</Text>
        )}
      </Box>

      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <HStack mb={4} justifyContent="space-between">
          <Text fontSize="lg" fontWeight="bold">
            Cap Table Funding ({capTableFunding.length})
          </Text>
        </HStack>

        {sortedFunding.length === 0 ? (
          <Text color="text.muted">No funding rounds found.</Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row bg="table.row">
                  <Table.ColumnHeader minW="150px">Label</Table.ColumnHeader>
                  <Table.ColumnHeader minW="110px">Issue Date</Table.ColumnHeader>
                  <Table.ColumnHeader minW="100px">Shares</Table.ColumnHeader>
                  <Table.ColumnHeader minW="110px">Total Shares</Table.ColumnHeader>
                  <Table.ColumnHeader minW="100px">Issue Price</Table.ColumnHeader>
                  <Table.ColumnHeader minW="220px">Direction</Table.ColumnHeader>
                  <Table.ColumnHeader minW="130px">Conversion Price</Table.ColumnHeader>
                  <Table.ColumnHeader minW="90px">Warrants</Table.ColumnHeader>
                  <Table.ColumnHeader minW="80px">Div Rate</Table.ColumnHeader>
                  <Table.ColumnHeader minW="90px">Dividends</Table.ColumnHeader>
                  <Table.ColumnHeader minW="60px">LP</Table.ColumnHeader>
                  <Table.ColumnHeader minW="120px">Liq Class Order</Table.ColumnHeader>
                  <Table.ColumnHeader minW="160px">Participating Preferred</Table.ColumnHeader>
                  <Table.ColumnHeader minW="130px">Participation Cap</Table.ColumnHeader>
                  <Table.ColumnHeader minW="140px">Ratchet Protection</Table.ColumnHeader>
                  <Table.ColumnHeader minW="100px">Edited By</Table.ColumnHeader>
                  <Table.ColumnHeader minW="110px">Created Date</Table.ColumnHeader>
                  <Table.ColumnHeader minW="120px">Modified Date</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedFunding.map((funding) => (
                  <Table.Row key={funding.id} bg="table.row" outline="1px solid" outlineColor="whiteAlpha.200">
                    <Table.Cell fontWeight="medium" whiteSpace="nowrap">{funding.label}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatDate(funding.issueDate)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.shares)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.totalShares)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatCurrency(funding.issuePrice)}</Table.Cell>
                    <Table.Cell>
                      {(() => {
                        const cmp = comparisonMap.get(funding.id)
                        if (!cmp || (cmp.ourDirection === null && cmp.pbDirection === null)) return "—"

                        const mit = mitigationMap.get(funding.id)

                        return (
                          <Stack gap={1}>
                            <HStack gap={1} flexWrap="wrap">
                              {cmp.ourDirection && (
                                <Text fontSize="xs" color={dirColor(cmp.ourDirection)} fontWeight="medium">
                                  {cmp.ourDirection}
                                </Text>
                              )}
                              {cmp.pbDirection && (
                                <>
                                  {cmp.ourDirection && <Text fontSize="xs" color="gray.400">|</Text>}
                                  <Text fontSize="xs" color={dirColor(cmp.pbDirection)} fontWeight="medium">
                                    PB: {cmp.pbDirection}
                                  </Text>
                                </>
                              )}
                              {cmp.isMismatch && !mit && (
                                <Box bg="rgba(245,158,11,0.15)" px={1.5} py={0.5} borderRadius="full">
                                  <Text fontSize="2xs" color="orange.600" fontWeight="bold">MISMATCH</Text>
                                </Box>
                              )}
                              {cmp.isMismatch && mit && (
                                <Box bg="rgba(34,197,94,0.15)" px={1.5} py={0.5} borderRadius="full">
                                  <Text fontSize="2xs" color="green.600" fontWeight="bold">MITIGATED</Text>
                                </Box>
                              )}
                            </HStack>

                            {cmp.isMismatch && (
                              <>
                                {mit ? (
                                  <HStack gap={1}>
                                    <Text fontSize="2xs" color="text.muted" title={`${mit.createdBy}: ${mit.memo}`}>
                                      {mit.memo.length > 30 ? mit.memo.slice(0, 30) + "..." : mit.memo}
                                    </Text>
                                    <Text
                                      fontSize="2xs"
                                      color="red.500"
                                      cursor="pointer"
                                      onClick={() => !savingMitigation && handleRemoveMitigation(funding.id)}
                                      _hover={{ textDecoration: "underline" }}
                                    >
                                      Remove
                                    </Text>
                                  </HStack>
                                ) : editingMitigationId === funding.id ? (
                                  <HStack gap={1}>
                                    <Input
                                      size="xs"
                                      placeholder="Mitigation memo..."
                                      value={mitigationMemo}
                                      onChange={(e) => setMitigationMemo(e.target.value)}
                                      onKeyDown={(e) => e.key === "Enter" && handleSaveMitigation(funding.id)}
                                      autoFocus
                                      maxW="180px"
                                    />
                                    <Text
                                      fontSize="2xs"
                                      color="blue.500"
                                      cursor="pointer"
                                      onClick={() => !savingMitigation && handleSaveMitigation(funding.id)}
                                      _hover={{ textDecoration: "underline" }}
                                    >
                                      Save
                                    </Text>
                                    <Text
                                      fontSize="2xs"
                                      color="gray.500"
                                      cursor="pointer"
                                      onClick={() => { setEditingMitigationId(null); setMitigationMemo("") }}
                                      _hover={{ textDecoration: "underline" }}
                                    >
                                      Cancel
                                    </Text>
                                  </HStack>
                                ) : (
                                  <Text
                                    fontSize="2xs"
                                    color="blue.500"
                                    cursor="pointer"
                                    onClick={() => { setEditingMitigationId(funding.id); setMitigationMemo("") }}
                                    _hover={{ textDecoration: "underline" }}
                                  >
                                    Mitigate
                                  </Text>
                                )}
                              </>
                            )}
                          </Stack>
                        )
                      })()}
                    </Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatCurrency(funding.conversionPrice)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.warrants)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.divRate)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.dividends)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.lp)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.liqClassOrder)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{funding.participationPreferred ? "Yes" : "No"}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatNumber(funding.participationCap)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{funding.ratchetProtection || "N/A"}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{funding.editedBy}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatDate(funding.createdAt)}</Table.Cell>
                    <Table.Cell whiteSpace="nowrap">{formatDate(funding.updatedAt)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Box>
    </Stack>
  )
}
